/**
 * Edit Sent Drop Modal – edit live SMSWiser drop (title, sender, texts, gateway, TLD).
 * Uses ScaleDropModalLayout 3-column: Config | Creative | Info.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { Route, Settings2, Sliders } from 'lucide-react';

import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { SearchableSelect } from '@/components/ui';
import { DropOptionsSection } from '@/features/smswiser-campaigns/components/drop-modals/sections/drop-options';
import { ScaleDropModalLayout } from '@/features/smswiser-campaigns/components/ScaleDropModal/ScaleDropModalLayout';
import { ColumnHeader } from '@/features/smswiser-campaigns/components/ScaleDropModal/shared';

import { EditSentDropCreativeColumn } from './EditSentDropCreativeColumn';
import { LEAVE_TLD_UNCHANGED } from './editSentDrop.constants';
import { useEditSentDropForm } from './useEditSentDropForm';
import type { EditSentDropModalProps } from './EditSentDropModal.types';

export type {
  EditSentDropPayload,
  EditSentDropModalProps,
  GatewayOption,
  SentEditSaveMeta,
  TldOption,
} from './EditSentDropModal.types';

export const EditSentDropModal: FC<EditSentDropModalProps> = (props) => {
  const { gateways, tlds, isBusy, onClose, onAddSender, onAddText, onDeleteTextsBulk } = props;
  const form = useEditSentDropForm(props);

  if (props.drops.length === 0) return null;

  return (
    <ScaleDropModalLayout
      title={form.isBulk ? `Edit sent drops — ${form.editableCount} selected` : `Edit sent drop #${form.drop?.sentDropId ?? ''}`}
      icon={<Settings2 className="h-4 w-4" />}
      onClose={onClose}
      isBusy={isBusy}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isBusy}>Cancel</Button>
          <Button onClick={form.handleSave} disabled={isBusy || !form.canSave}>
            {form.isBulk ? `Apply to ${form.editableCount} drop${form.editableCount !== 1 ? 's' : ''}` : 'Save'}
          </Button>
        </>
      }
      configSection={
        <div className="flex flex-1 flex-col">
          <ColumnHeader icon={Settings2} title="Configuration" subtitle="Gateway & TLD" />
          <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden p-3">
            <div className="rounded-md bg-muted/30 p-2.5 text-sm">
              <p className="font-medium text-foreground">
                {form.isBulk ? `Editing ${form.editableCount} sent drop${form.editableCount !== 1 ? 's' : ''}` : 'Editing sent drop'}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {form.isBulk ? 'Only filled fields will be applied. Leave blank to keep unchanged.' : 'Only editable fields are shown.'}
              </p>
            </div>
            {!form.isBulk && (
              <div>
                <label className="block text-xs font-medium text-muted-foreground">Title</label>
                <Input className="mt-1 h-10" value={form.title} onChange={(e) => form.setTitle(e.target.value)} placeholder="Drop title" />
              </div>
            )}
            <section className="space-y-2">
              <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Route className="h-3.5 w-3.5" /> Gateway & TLD
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <SearchableSelect<number>
                  options={form.isBulk
                    ? [{ value: -1, label: '— Leave unchanged —' }, ...gateways.map((g) => ({ value: g.id, label: g.name }))]
                    : [{ value: 0, label: 'Select gateway' }, ...gateways.map((g) => ({ value: g.id, label: g.name }))]
                  }
                  value={(form.isBulk ? (form.routeId === '' ? -1 : form.routeId) : (form.routeId === '' ? 0 : form.routeId)) as number}
                  onChange={(v) => form.setRouteId(form.isBulk && v === -1 ? '' : v)}
                  placeholder={form.isBulk ? 'Leave unchanged' : 'Select gateway'}
                  searchPlaceholder="Search gateways..."
                />
                <SearchableSelect<string>
                  options={form.isBulk
                    ? [{ value: LEAVE_TLD_UNCHANGED, label: '— Leave unchanged —' }, ...tlds.map((t) => ({ value: t.value, label: t.displayName }))]
                    : [{ value: '', label: 'Select TLD' }, ...tlds.map((t) => ({ value: t.value, label: t.displayName }))]
                  }
                  value={form.isBulk ? (form.tldValue || LEAVE_TLD_UNCHANGED) : form.tldValue}
                  onChange={(v) => form.setTldValue(v ?? '')}
                  placeholder={form.isBulk ? 'Leave unchanged' : 'Select TLD'}
                  searchPlaceholder="Search TLDs..."
                />
              </div>
            </section>
          </div>
          <div className="shrink-0 border-t border-border/40">
            <div className="flex items-center gap-1.5 bg-muted/20 px-4 py-2">
              <Sliders className="h-3.5 w-3.5 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Drop Options</span>
            </div>
            <div className="p-3">
              <DropOptionsSection
                hideTitle
                compact
                showOnlyForceSend
                allowUnchanged={form.isBulk}
                amount={0}
                forceSend={form.forceSend}
                setForceSend={form.setForceSend}
                sendInBatches={form.isBulk ? 'unchanged' : false}
                setSendInBatches={() => {}}
                batchAmount={undefined}
                setBatchAmount={() => {}}
                batchDelay={undefined}
                setBatchDelay={() => {}}
                aiOptimizationEnabled={false}
                setAiOptimizationEnabled={() => {}}
                aiOptimizationType=""
                setAiOptimizationType={() => {}}
                aiOptimizationBatchAmount=""
                setAiOptimizationBatchAmount={() => {}}
                aiOptimizationMinimalValuePercent=""
                setAiOptimizationMinimalValuePercent={() => {}}
                aiOptimizationSendUntil=""
                setAiOptimizationSendUntil={() => {}}
                useOnlyNewDomains={form.useOnlyNewDomains}
                setUseOnlyNewDomains={form.setUseOnlyNewDomains}
                shortlink={form.shortlink}
                isBusy={isBusy}
              />
            </div>
          </div>
        </div>
      }
      creativeSection={
        <EditSentDropCreativeColumn
          drops={props.drops}
          form={form}
          isBusy={isBusy}
          countryKey={props.countryKey}
          onAddSender={onAddSender}
          onAddText={onAddText}
          onDeleteTextsBulk={onDeleteTextsBulk}
          tagsByTextId={props.tagsByTextId}
          tagColors={props.tagColors}
          smsTextsWithIds={props.smsTextsWithIds}
        />
      }
      leadsSection={
        <>
          <ColumnHeader icon={Settings2} title="Drop Info" />
          <div className="flex flex-1 flex-col items-center justify-center gap-2 p-6 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">
              {form.isBulk ? `${form.editableCount} drop${form.editableCount !== 1 ? 's' : ''} selected` : `Drop #${form.drop?.sentDropId}`}
            </p>
            <p className="text-xs">
              {form.isBulk ? 'Select only what to change. Leave blank = keep each drop as is.' : 'Update title, gateway, TLD, sender and texts.'}
            </p>
          </div>
        </>
      }
    />
  );
};

export default EditSentDropModal;
